//ol lib includes
import 'ol/ol.css';
import GeoJSON from 'ol/format/GeoJSON';
import { Fill, Stroke, Style } from 'ol/style';
import { Map, View } from 'ol';
import {
  Vector as VectorSource,
  Raster as RasterSource,
  TileWMS,
  OSM,
  XYZ 
} from 'ol/source';

import {
  Vector as VectorLayer,
  Image as ImageLayer,
  Tile as TileLayer
} from 'ol/layer';

import Projection from 'ol/proj/Projection';
import proj4 from 'proj4';
import { register } from 'ol/proj/proj4';

//app js
import { mapServices } from '../config/dataservices';
import { DataService } from './data.service';
import { RasterOperations } from './raster-operation.service';
import { ImageProcessingLibs } from './raster-operation.service';

//define OpenLayer Constants
let LandCoverSources = {};
let landcoverYear = mapServices.startYear;
while (landcoverYear <= mapServices.endYear) {
  LandCoverSources[landcoverYear] = new TileWMS({
    url: mapServices.base_url,
    params:{'LAYERS': mapServices.landcover[landcoverYear], 'TILED': true, 'STYLES': '', srs:"EPSG:5070", TIME:landcoverYear.toString() + '-01-01'},
    crossOrigin: 'anonymous',
    serverType: 'geoserver'
  })
  landcoverYear++;
}

let BasemapSources = {
  'satellite': new XYZ({
    url: mapServices.satelliteBaseMapURL,
    crossOrigin: 'anonymous'
  }),
  'streets':new OSM({
    url: mapServices.streetsBaseMapURL,
    crossOrigin: 'anonymous'
  })
}

proj4.defs('EPSG:5070', '+proj=aea +lat_1=29.5 +lat_2=45.5 +lat_0=23 +lon_0=-96 +x_0=0 +y_0=0 +datum=NAD83 +units=m +no_defs');
register(proj4);

export class MapService {
  constructor(options) {
    options = options || {};
    this.map = null;
    this.mapElementRef = options.mapElementRef || 'map';
    this.center = options.center || [-75195, 2013645];
    this.zoom = options.zoom || 4;
    this.minZoom = options.minZoom || 2;
    this.maxZoom = options.maxZoom || 15;
    this.layers = options.layers || {};
    this.dataService = new DataService();
	this.changeLayerVisible = true;

    this.dataService.on("geoidChange", (event, response) => {
      if (response.error) {
        console.error(`WEB SERVICE ERROR: ${event}`, response.error.data);
      }
      else {
        //console.log(`${event}`, response.data);
        let extent = this.showFootprint(response.data);
        this.dataService.setLocationExtent(extent);
      }
    });

    this.dataService.on('fromYearChange', (event, response) => {
      this.updateFromYearLayer(response.data.newValue);
    });

    this.dataService.on('toYearChange', (event, response) => {
      this.updateToYearLayer(response.data.newValue);
    });

    this.dataService.on('tabChange', (event, response) => {
      this.updateMapLayerForTab(response.data.tab);
    });

    this.dataService.on('landCoverLayerChange', (event, response) => {
      this.updateLandCoverBaseLayer(response.data.landCoverLayer);
    });

    this.dataService.on('basemapChange', (event, response) => {
      this.updateBasemap(response.data.basemap);
    });
	this.dataService.on('toggleDifferenceLayer', (event, response) => {
      this.toggleDifferenceLayer(response);
    });
  }
  
  toggleDifferenceLayer() {
	if (this.changeLayerVisible) {
	  this.changeLayerVisible = false;
	} else {
	  this.changeLayerVisible = true;
	}
	this.differenceImageLayer.setVisible(this.changeLayerVisible);
	// this.differenceImageLayer.setSource(this.developedChangesRasterSource);
  }

  updateLandCoverBaseLayer(landCoverLayer) {
    this.landCoverBaseLayer.setVisible(landCoverLayer !== 'none');
    this.landCoverBaseLayer.setSource(LandCoverSources[+landCoverLayer]);
  }

  updateBasemap(basemap) {
    this.basemapLayer.setSource(BasemapSources[basemap]);
  }

  updateMapLayerForTab(tab) {
    if (tab === 'general') {
      this.differenceImageLayer.setSource(this.landCoverChangeRasterSource);
    }
    if (tab === 'developed') {
      this.differenceImageLayer.setSource(this.developedChangesRasterSource);
    }
    if (tab === 'forests') {
      this.differenceImageLayer.setSource(this.forestsChangesRasterSource);
    }
    if (tab === 'wetlands') {
      this.differenceImageLayer.setSource(this.wetlandsChangesRasterSource);
    }
    if (tab === 'agriculture') {
        this.differenceImageLayer.setSource(this.agricultureChangesRasterSource);
      }
    }

  updateFromYearLayer(year) {
    this.fromLandCoverLayer.setSource(LandCoverSources[year]);
  }

  updateToYearLayer(year) {
    this.toLandCoverLayer.setSource(LandCoverSources[year]);
  }

  initialize() {

    this.basemapLayer = new TileLayer({
      source: BasemapSources['satellite']
    });

    this.landCoverBaseLayer = new TileLayer({
      opacity: 0.8,
      visible: false,
      source: LandCoverSources[1985]
    });

    this.fromLandCoverLayer = new TileLayer({
      opacity: 1,
      source: LandCoverSources[1985]
    });
    this.toLandCoverLayer = new TileLayer({
      opacity: 1,
      source: LandCoverSources[2025]
    });

    this.landCoverChangeRasterSource = new RasterSource({
      sources: [this.fromLandCoverLayer, this.toLandCoverLayer],
      opaque: true,
      operation: RasterOperations.generalChange,
      lib: ImageProcessingLibs
    });

    this.developedChangesRasterSource = new RasterSource({
      sources: [this.fromLandCoverLayer, this.toLandCoverLayer],
      opaque: true,
      operation: RasterOperations.developedChange,
      lib: ImageProcessingLibs
    });

    this.forestsChangesRasterSource = new RasterSource({
      sources: [this.fromLandCoverLayer, this.toLandCoverLayer],
      opaque: true,
      operation: RasterOperations.forestsChange,
      lib: ImageProcessingLibs
    });

    this.wetlandsChangesRasterSource = new RasterSource({
      sources: [this.fromLandCoverLayer, this.toLandCoverLayer],
      opaque: true,
      operation: RasterOperations.wetlandsChange,
      lib: ImageProcessingLibs
    });
	
    this.agricultureChangesRasterSource = new RasterSource({
        sources: [this.fromLandCoverLayer, this.toLandCoverLayer],
        opaque: true,
        operation: RasterOperations.agricultureChange,
        lib: ImageProcessingLibs
    });

    this.differenceImageLayer = new ImageLayer({
      opacity: 1,
      source: this.landCoverChangeRasterSource
    })

    this.footprintLayer = new VectorLayer({
      source: new VectorSource({
        features: []
      }),
      style: new Style({
        stroke: new Stroke({
          color: [52, 196, 255, 1],
          width: 2
        }),
        fill: new Fill({
          color: [52, 196, 255, 0]
        })
      })
    });

    this.map = new Map({
      target: this.mapElementRef,
      loadTilesWhileAnimating: true,
      loadTilesWhileInteracting: true,
      layers: [
        this.basemapLayer,
        this.landCoverBaseLayer,
        this.differenceImageLayer,
        this.footprintLayer
      ],
      view: new View({
        center: this.center,
        extent: [-2493045, 147285, 2342655, 4050005],
        zoom: this.zoom,
        minZoom: this.minZoom,
        maxZoom: this.maxZoom,
        projection: 'EPSG:5070'
      })
    });

    //this lets you disable rendering the raster image processing layer 
    //in local development mode. find the variable below in .env.development file
    //this is needed because of parallel worker, chrome dev tools seem to slow down 
    //could be a temp issue when this was written. check the need in future. The code below
    //wont be added when this is built for production, safe to delete anytime. 
    if (process.env.DISABLE_CHANGE_RASTER === 'yes') {
      this.map.removeLayer(this.differenceImageLayer);
    }

  }

  showFootprint(data) {
    this.footprintLayer.getSource().clear();
    this.footprintLayer.getSource().addFeatures((new GeoJSON()).readFeatures(data));
    this.map.getView().fit(this.footprintLayer.getSource().getExtent(), { duration: 1000 });
    return this.footprintLayer.getSource().getExtent();
  }
}